import { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, Plus, Trash2, CreditCard, Banknote, Search, User, UserPlus, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { patchReadCache, isOffline } from '../lib/axiosOffline';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));

export const PuntoVenta = () => {
    const [inventario, setInventario] = useState([]);
    const [ventas, setVentas] = useState([]);
    const [resumen, setResumen] = useState({});
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState([]);
    const [cliente, setCliente] = useState('');
    const [metodoPago, setMetodoPago] = useState('efectivo');
    const [searchTerm, setSearchTerm] = useState('');
    const [aCredito, setACredito] = useState(false);
    const [clienteId, setClienteId] = useState('');
    const [clientesList, setClientesList] = useState([]);
    const [abonoInicial, setAbonoInicial] = useState(0);
    // Autocomplete de cliente + creacion inline
    const [clienteQuery, setClienteQuery] = useState('');
    const [showClienteOptions, setShowClienteOptions] = useState(false);
    const [showCrearCliente, setShowCrearCliente] = useState(false);
    const [nuevoCliente, setNuevoCliente] = useState({ nombre: '', telefono: '', notas: '' });
    const [creandoCliente, setCreandoCliente] = useState(false);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [invRes, ventasRes, resumenRes, clientesRes] = await Promise.all([
                axios.get(`${API_URL}/api/producto-terminado/inventario`),
                axios.get(`${API_URL}/api/ventas`),
                axios.get(`${API_URL}/api/ventas/resumen`),
                axios.get(`${API_URL}/api/clientes`).catch(() => ({ data: [] }))
            ]);
            setInventario(invRes.data);
            setVentas(ventasRes.data);
            setResumen(resumenRes.data);
            setClientesList(clientesRes.data || []);
        } catch (e) { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    };

    const addToCart = (item) => {
        const existing = cart.find(c => c.modelo === item.modelo && c.talla === item.talla && c.color === item.color && (c.modo || 'pieza') === 'pieza');
        if (existing) {
            if (existing.cantidad >= item.stock) { toast.error('Sin stock disponible'); return; }
            setCart(cart.map(c => c === existing ? { ...c, cantidad: c.cantidad + 1, subtotal: (c.cantidad + 1) * c.precio_unitario } : c));
        } else {
            setCart([...cart, {
                nombre: item.modelo, talla: item.talla, color: item.color,
                cantidad: 1, precio_unitario: 0, subtotal: 0, stock: item.stock,
                modo: 'pieza', paquetes: 0, tamano_paquete: 14
            }]);
        }
    };

    // Cicla entre modos: 'pieza' → 'paquete' → 'corrida' → 'pieza'.
    // - pieza: 1 pza
    // - paquete: N paquetes × tamano_paquete pzs (14 adulto, 16 nino)
    // - corrida: N corridas × (tamano_paquete / 2) pzs (la mitad del paquete)
    const toggleModoVenta = (idx) => {
        setCart(cart.map((c, i) => {
            if (i !== idx) return c;
            const tamano = c.tamano_paquete || 14;
            const corridaPzs = Math.ceil(tamano / 2);
            const modoActual = c.modo || 'pieza';
            if (modoActual === 'pieza') {
                return { ...c, modo: 'paquete', paquetes: 1, tamano_paquete: tamano, cantidad: tamano, subtotal: (c.precio_unitario || 0) * 1 };
            }
            if (modoActual === 'paquete') {
                return { ...c, modo: 'corrida', paquetes: 1, tamano_paquete: tamano, cantidad: corridaPzs, subtotal: (c.precio_unitario || 0) * 1 };
            }
            return { ...c, modo: 'pieza', paquetes: 0, cantidad: 1, subtotal: c.precio_unitario || 0 };
        }));
    };

    const cambiarTamanoPaquete = (idx, nuevoTamano) => {
        setCart(cart.map((c, i) => {
            if (i !== idx) return c;
            const paq = c.paquetes || 1;
            const piezasPorUnidad = (c.modo || 'pieza') === 'corrida' ? Math.ceil(nuevoTamano / 2) : nuevoTamano;
            return { ...c, tamano_paquete: nuevoTamano, cantidad: paq * piezasPorUnidad };
        }));
    };

    const addManualItem = () => {
        setCart([...cart, {
            nombre: 'Articulo libre',
            talla: '-',
            color: '-',
            cantidad: 1,
            precio_unitario: 0,
            subtotal: 0,
            stock: 9999,
            manual: true,  // backend NO descuenta inventario
            modo: 'pieza', paquetes: 0, tamano_paquete: 14
        }]);
    };

    const updateCartItem = (idx, field, value) => {
        setCart(cart.map((c, i) => {
            if (i !== idx) return c;
            const updated = { ...c, [field]: value };
            const modo = updated.modo || 'pieza';
            if (modo === 'paquete' || modo === 'corrida') {
                // El campo 'cantidad' del input se interpreta como N paquetes/corridas.
                const tamano = updated.tamano_paquete || 14;
                const piezasPorUnidad = modo === 'corrida' ? Math.ceil(tamano / 2) : tamano;
                if (field === 'cantidad') {
                    const paq = parseFloat(value) || 0;
                    updated.paquetes = paq;
                    updated.cantidad = paq * piezasPorUnidad;
                }
                const paq = updated.paquetes || 0;
                updated.subtotal = paq * (parseFloat(updated.precio_unitario) || 0);
            } else if (field === 'cantidad' || field === 'precio_unitario') {
                updated.subtotal = (parseFloat(updated.cantidad) || 0) * (parseFloat(updated.precio_unitario) || 0);
            }
            return updated;
        }));
    };

    const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));
    const cartTotal = cart.reduce((s, c) => s + (c.subtotal || 0), 0);

    const handleVenta = async () => {
        // Cliente: si hay seleccion registrada (clienteId), usar su nombre.
        // Si no, usar el texto libre del autocomplete.
        const clienteSeleccionado = clienteId ? clientesList.find(c => c.id === clienteId) : null;
        const nombreCliente = clienteSeleccionado?.nombre || (clienteQuery || '').trim() || cliente;
        if (!nombreCliente && !aCredito) { toast.error('Ingrese nombre del cliente'); return; }
        if (aCredito && !clienteId) { toast.error('Selecciona un cliente registrado (o crealo) para venta a credito'); return; }
        if (cart.length === 0) { toast.error('Agregue productos al carrito'); return; }
        if (cart.some(c => !c.precio_unitario || c.precio_unitario <= 0)) { toast.error('Todos los productos deben tener precio'); return; }
        const offline = isOffline();
        try {
            const venta_id = newId();
            const credito_id = aCredito ? newId() : '';
            const abono_id = aCredito && parseFloat(abonoInicial) > 0 ? newId() : '';
            const payload = {
                id: venta_id,
                cliente: nombreCliente,
                items: cart.map(({ stock, ...rest }) => rest),
                total: cartTotal,
                metodo_pago: metodoPago,
                a_credito: aCredito,
                cliente_id: aCredito ? clienteId : '',
                abono_inicial: aCredito ? (parseFloat(abonoInicial) || 0) : 0,
                credito_id,
                abono_id
            };
            const res = await axios.post(`${API_URL}/api/ventas`, payload);
            if (res?.data?._offline) {
                // Optimistic local: agregar a la lista de ventas para que se vea
                const nuevaVenta = {
                    ...payload,
                    vendedor_nombre: 'Tu (offline)',
                    created_at: new Date().toISOString()
                };
                setVentas(prev => [nuevaVenta, ...prev]);
                patchReadCache('/api/ventas', curr => Array.isArray(curr) ? [nuevaVenta, ...curr] : [nuevaVenta]);
                // Si fue a credito, actualizar tambien la lista de clientes en cache
                if (aCredito && clienteSeleccionado) {
                    const monto = cartTotal;
                    const abono = parseFloat(abonoInicial) || 0;
                    const updateCliente = (c) => c.id === clienteId ? {
                        ...c,
                        total_facturado: (c.total_facturado || 0) + monto,
                        total_abonado: (c.total_abonado || 0) + abono,
                        saldo_pendiente: (c.saldo_pendiente || 0) + monto - abono,
                        num_creditos: (c.num_creditos || 0) + 1,
                        num_creditos_pendientes: (c.num_creditos_pendientes || 0) + (abono >= monto - 0.01 ? 0 : 1)
                    } : c;
                    setClientesList(prev => prev.map(updateCliente));
                    patchReadCache('/api/clientes', curr => Array.isArray(curr) ? curr.map(updateCliente) : curr);
                }
                toast.success(aCredito ? 'Venta a credito guardada offline (se sincronizara)' : 'Venta guardada offline (se sincronizara)');
            } else {
                toast.success(aCredito ? 'Venta a credito registrada' : 'Venta registrada');
            }
            setCart([]);
            setCliente('');
            setACredito(false);
            setClienteId('');
            setAbonoInicial(0);
            setClienteQuery('');
            setShowClienteOptions(false);
            if (!offline) fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar venta'); }
    };

    // Crear cliente inline desde POS
    const crearClienteRapido = async () => {
        const nombre = (nuevoCliente.nombre || '').trim();
        if (!nombre) { toast.error('El nombre es obligatorio'); return; }
        setCreandoCliente(true);
        try {
            const payload = {
                nombre,
                telefono: (nuevoCliente.telefono || '').trim(),
                notas: (nuevoCliente.notas || '').trim()
            };
            const r = await axios.post(`${API_URL}/api/clientes`, payload);
            const c = r.data;
            if (!c || !c.id) { toast.error('No se pudo crear el cliente'); return; }
            setClientesList(prev => [c, ...prev]);
            setClienteId(c.id);
            setCliente(c.nombre);
            setClienteQuery(c.nombre);
            setShowCrearCliente(false);
            setShowClienteOptions(false);
            setNuevoCliente({ nombre: '', telefono: '', notas: '' });
            toast.success(`Cliente "${c.nombre}" creado y seleccionado`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear cliente');
        } finally {
            setCreandoCliente(false);
        }
    };

    // Selecciona cliente existente desde el autocomplete
    const seleccionarCliente = (c) => {
        if (!c || !c.id) return;
        setClienteId(c.id);
        setCliente(c.nombre || '');
        setClienteQuery(c.nombre || '');
        setShowClienteOptions(false);
    };

    const limpiarCliente = () => {
        setClienteId('');
        setCliente('');
        setClienteQuery('');
        setShowClienteOptions(false);
    };

    const filteredInv = inventario.filter(i => i.stock > 0 && (
        (i.modelo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.color || '').toLowerCase().includes(searchTerm.toLowerCase())
    ));

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="pos-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Punto de Venta</h1>
                    <p className="text-zinc-400 mt-1">Registrar ventas y descontar inventario</p>
                </div>
                <div className="flex gap-4 text-sm">
                    <div className="text-center px-4 py-2 rounded-lg bg-zinc-800 border border-zinc-700">
                        <p className="text-zinc-400">Ventas Hoy</p>
                        <p className="text-xl font-bold text-amber-400">{resumen.ventas_hoy || 0}</p>
                    </div>
                    <div className="text-center px-4 py-2 rounded-lg bg-zinc-800 border border-zinc-700">
                        <p className="text-zinc-400">Total Hoy</p>
                        <p className="text-xl font-bold text-emerald-400">${(resumen.total_hoy || 0).toFixed(2)}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT: Product Selection */}
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <Input placeholder="Buscar producto..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-pos" />
                        </div>
                        <Button onClick={addManualItem} variant="outline" className="shrink-0 border-amber-400/50 text-amber-400 hover:bg-amber-400/10" data-testid="btn-item-manual">
                            <Plus className="w-4 h-4 mr-1" /> Item libre
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto pr-1">
                        {filteredInv.map((item, idx) => (
                            <button key={idx} onClick={() => addToCart(item)}
                                className="p-3 rounded-lg bg-zinc-800/80 border border-zinc-700 hover:border-amber-400/50 hover:bg-zinc-800 transition-all text-left group" data-testid={`pos-item-${idx}`}>
                                <p className="text-sm font-medium text-zinc-200 group-hover:text-amber-400 transition-colors">{item.modelo}</p>
                                <p className="text-xs text-zinc-500">{item.talla} · {item.color}</p>
                                <p className="text-sm font-bold text-emerald-400 mt-1">{item.stock} disp.</p>
                            </button>
                        ))}
                        {filteredInv.length === 0 && <p className="col-span-full text-center text-zinc-500 py-8">Sin productos disponibles</p>}
                    </div>
                </div>

                {/* RIGHT: Cart */}
                <Card className="card">
                    <CardContent className="p-4 space-y-4">
                        <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-amber-400" /> Carrito
                        </h2>
                        <div className="form-group">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="form-label !mb-0">Cliente *</Label>
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs text-zinc-400 cursor-pointer">A credito</Label>
                                    <Switch checked={aCredito} onCheckedChange={(v) => { setACredito(v); if (!v) { setClienteId(''); setAbonoInicial(0); } }} data-testid="switch-credito" />
                                </div>
                            </div>
                            {/* Cliente: autocomplete unificado + crear inline. Funciona para
                                ventas normales y a credito. */}
                            <div className="space-y-2">
                                {(() => {
                                    const term = (clienteQuery || '').trim().toLowerCase();
                                    const matches = !term
                                        ? clientesList.slice(0, 8)
                                        : clientesList.filter(c =>
                                            c && c.id && (
                                                (c.nombre || '').toLowerCase().includes(term) ||
                                                (c.telefono || '').toLowerCase().includes(term)
                                            )
                                        ).slice(0, 8);
                                    return (
                                        <div className="relative">
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                                <Input
                                                    value={clienteQuery}
                                                    onChange={(e) => {
                                                        setClienteQuery(e.target.value);
                                                        setShowClienteOptions(true);
                                                        // Si el usuario edita, limpia la seleccion previa
                                                        if (clienteId) {
                                                            setClienteId('');
                                                            setCliente('');
                                                        }
                                                    }}
                                                    onFocus={() => setShowClienteOptions(true)}
                                                    placeholder={aCredito ? 'Busca o crea cliente registrado...' : 'Nombre del cliente (libre o registrado)'}
                                                    className="pl-9"
                                                    data-testid="input-cliente-pos"
                                                />
                                                {clienteId && (
                                                    <button type="button" onClick={limpiarCliente} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200" title="Limpiar">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                            {clienteId && (
                                                <div className="mt-1 text-[11px] text-emerald-400 flex items-center gap-1">
                                                    ✓ Cliente registrado seleccionado
                                                </div>
                                            )}
                                            {showClienteOptions && !clienteId && (
                                                <div className="absolute z-20 left-0 right-0 mt-1 max-h-[260px] overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-md shadow-xl" data-testid="lista-clientes-pos">
                                                    {matches.length > 0 && matches.map((c) => (
                                                        <button
                                                            key={c.id}
                                                            type="button"
                                                            onClick={() => seleccionarCliente(c)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 transition flex items-center justify-between gap-2 border-b border-zinc-800/60 last:border-b-0"
                                                            data-testid={`opcion-cliente-${c.id}`}
                                                        >
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-zinc-100 font-medium truncate">{c.nombre || '(sin nombre)'}</p>
                                                                {c.telefono && <p className="text-[11px] text-zinc-500 truncate">{c.telefono}</p>}
                                                            </div>
                                                            {(c.saldo_pendiente || 0) > 0.01 && (
                                                                <span className="text-[11px] text-red-400 shrink-0">debe ${(c.saldo_pendiente).toFixed(0)}</span>
                                                            )}
                                                        </button>
                                                    ))}
                                                    {matches.length === 0 && (
                                                        <div className="px-3 py-3 text-xs text-zinc-500">
                                                            {term ? `Sin coincidencias para "${term}"` : 'Sin clientes registrados'}
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setNuevoCliente({ nombre: clienteQuery, telefono: '', notas: '' });
                                                            setShowCrearCliente(true);
                                                            setShowClienteOptions(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm text-amber-400 hover:bg-amber-400/10 transition border-t border-zinc-700 flex items-center gap-2"
                                                        data-testid="btn-crear-cliente-pos"
                                                    >
                                                        <UserPlus className="w-4 h-4" />
                                                        {clienteQuery ? `Crear "${clienteQuery}" como nuevo cliente` : 'Crear cliente nuevo...'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                {aCredito && (
                                    <>
                                        {!clienteId && (
                                            <p className="text-[11px] text-amber-400">Para venta a credito debes seleccionar o crear un cliente registrado.</p>
                                        )}
                                        <Input
                                            type="number" step="0.01" min="0" max={cartTotal}
                                            value={abonoInicial}
                                            onChange={(e) => setAbonoInicial(parseFloat(e.target.value) || 0)}
                                            placeholder="Abono inicial (opcional)"
                                            data-testid="input-abono-inicial"
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        {cart.length > 0 ? (
                            <div className="space-y-2 max-h-[280px] overflow-y-auto">
                                {cart.map((item, idx) => {
                                    const modo = item.modo || 'pieza';
                                    const tamano = item.tamano_paquete || 14;
                                    const valorCantidad = (modo === 'paquete' || modo === 'corrida') ? (item.paquetes || 1) : item.cantidad;
                                    const corridaPzs = Math.ceil(tamano / 2);
                                    const modoBadge = modo === 'paquete' ? `PAQ ×${tamano}` : modo === 'corrida' ? `CORRIDA ×${corridaPzs}` : 'PZA';
                                    const modoBadgeBg = modo === 'paquete' ? 'bg-amber-400 text-zinc-900' : modo === 'corrida' ? 'bg-blue-400 text-zinc-900' : 'bg-zinc-700 text-zinc-300';
                                    return (
                                    <div key={idx} className={`p-2 rounded-lg ${item.manual ? 'bg-amber-400/5 border border-amber-400/30' : 'bg-zinc-800/50 border border-zinc-700'}`}>
                                        {item.manual ? (
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300">LIBRE</span>
                                                    <Input value={item.nombre} onChange={e => updateCartItem(idx, 'nombre', e.target.value)} placeholder="Descripcion" className="flex-1 h-7 text-sm" data-testid={`manual-nombre-${idx}`} />
                                                    <button onClick={() => removeFromCart(idx)} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Input value={item.talla} onChange={e => updateCartItem(idx, 'talla', e.target.value)} placeholder="Talla" className="w-16 h-7 text-xs" />
                                                    <Input value={item.color} onChange={e => updateCartItem(idx, 'color', e.target.value)} placeholder="Color" className="w-20 h-7 text-xs" />
                                                    <button type="button" onClick={() => toggleModoVenta(idx)} className={`text-[10px] px-1.5 py-1 rounded font-bold ${modoBadgeBg}`} data-testid={`btn-modo-${idx}`} title="Cambiar pza / paquete / corrida">
                                                        {modoBadge}
                                                    </button>
                                                    {(modo === 'paquete' || modo === 'corrida') && (
                                                        <select value={tamano} onChange={e => cambiarTamanoPaquete(idx, parseInt(e.target.value))} className="bg-zinc-700 text-xs rounded px-1 h-7 text-zinc-200 border border-zinc-600">
                                                            <option value={14}>14 (Adulto)</option>
                                                            <option value={16}>16 (Niño)</option>
                                                            <option value={18}>18</option>
                                                        </select>
                                                    )}
                                                    <Input type="number" min="1" value={valorCantidad} onChange={e => updateCartItem(idx, 'cantidad', parseInt(e.target.value) || 1)} className="w-12 text-center h-7 text-xs" />
                                                    <Input type="number" step="0.01" min="0" value={item.precio_unitario} onChange={e => updateCartItem(idx, 'precio_unitario', parseFloat(e.target.value) || 0)} placeholder={modo === 'paquete' ? '$/paq' : modo === 'corrida' ? '$/corrida' : '$'} className="w-20 text-center h-7 text-xs" />
                                                    <span className="text-sm font-medium text-amber-400 w-16 text-right">${(item.subtotal || 0).toFixed(0)}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-zinc-200 truncate">{item.nombre}</p>
                                                        <p className="text-xs text-zinc-500">{item.talla} · {item.color}{(modo === 'paquete' || modo === 'corrida') ? ` · ${item.cantidad} pzas total` : ''}</p>
                                                    </div>
                                                    <button onClick={() => removeFromCart(idx)} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <button type="button" onClick={() => toggleModoVenta(idx)} className={`text-[10px] px-2 py-1 rounded font-bold ${modoBadgeBg}`} data-testid={`btn-modo-${idx}`} title="Cambiar pza / paquete / corrida">
                                                        {modo === 'paquete' ? `PAQ ×${tamano}` : modo === 'corrida' ? `CORRIDA ×${corridaPzs}` : 'POR PZA'}
                                                    </button>
                                                    {(modo === 'paquete' || modo === 'corrida') && (
                                                        <select value={tamano} onChange={e => cambiarTamanoPaquete(idx, parseInt(e.target.value))} className="bg-zinc-700 text-xs rounded px-1 h-8 text-zinc-200 border border-zinc-600">
                                                            <option value={14}>14 (Adulto)</option>
                                                            <option value={16}>16 (Niño)</option>
                                                            <option value={18}>18</option>
                                                        </select>
                                                    )}
                                                    <Input type="number" min="1" max={modo === 'pieza' ? item.stock : undefined} value={valorCantidad} onChange={e => updateCartItem(idx, 'cantidad', parseInt(e.target.value) || 1)} className="w-16 text-center h-8 text-sm" />
                                                    <Input type="number" step="0.01" min="0" value={item.precio_unitario} onChange={e => updateCartItem(idx, 'precio_unitario', parseFloat(e.target.value) || 0)} placeholder={modo === 'paquete' ? '$/paq' : modo === 'corrida' ? '$/corrida' : '$'} className="w-20 text-center h-8 text-sm" />
                                                    <span className="text-sm font-medium text-amber-400 w-16 text-right">${(item.subtotal || 0).toFixed(0)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-center text-zinc-500 py-6">Seleccione productos o agregue un item libre</p>
                        )}

                        <div className="border-t border-zinc-700 pt-3 space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-400">Total:</span>
                                <span className="text-2xl font-bold text-amber-400">${cartTotal.toFixed(2)}</span>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Metodo de Pago</Label>
                                <Select value={metodoPago} onValueChange={setMetodoPago}>
                                    <SelectTrigger data-testid="select-metodo-pago"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="efectivo">Efectivo</SelectItem>
                                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                                        <SelectItem value="transferencia">Transferencia</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button onClick={handleVenta} className={`w-full h-11 font-bold ${aCredito ? 'bg-amber-500 hover:bg-amber-600 text-zinc-900' : 'bg-emerald-500 hover:bg-emerald-600'}`} disabled={cart.length === 0} data-testid="btn-cobrar">
                                <CreditCard className="w-5 h-5 mr-2" />
                                {aCredito
                                    ? `Registrar credito ${abonoInicial > 0 ? `(abono $${parseFloat(abonoInicial).toFixed(2)})` : '(sin abono)'}`
                                    : `Cobrar $${cartTotal.toFixed(2)}`}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Sales */}
            <Card className="card">
                <CardContent className="p-4">
                    <h3 className="text-lg font-bold text-zinc-100 mb-4">Ventas Recientes</h3>
                    <div className="overflow-x-auto">
                        <Table><TableHeader><TableRow className="bg-zinc-800">
                            <TableHead className="table-header">Fecha</TableHead>
                            <TableHead className="table-header">Cliente</TableHead>
                            <TableHead className="table-header">Items</TableHead>
                            <TableHead className="table-header">Total</TableHead>
                            <TableHead className="table-header">Pago</TableHead>
                            <TableHead className="table-header">Vendedor</TableHead>
                        </TableRow></TableHeader><TableBody>
                            {ventas.slice(0, 20).map(v => (
                                <TableRow key={v.id} className="table-row">
                                    <TableCell className="table-cell">{v.created_at?.split('T')[0]}</TableCell>
                                    <TableCell className="table-cell font-medium">{v.cliente}</TableCell>
                                    <TableCell className="table-cell">{v.items?.length || 0} productos</TableCell>
                                    <TableCell className="table-cell"><span className="text-emerald-400 font-bold">${v.total?.toFixed(2)}</span></TableCell>
                                    <TableCell className="table-cell"><span className="badge-info capitalize">{v.metodo_pago}</span></TableCell>
                                    <TableCell className="table-cell">{v.vendedor_nombre || '-'}</TableCell>
                                </TableRow>
                            ))}
                            {ventas.length === 0 && (
                                <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-400">Sin ventas registradas</TableCell></TableRow>
                            )}
                        </TableBody></Table>
                    </div>
                </CardContent>
            </Card>

            {/* Modal Crear Cliente Rapido */}
            <Dialog open={showCrearCliente} onOpenChange={(o) => { if (!o) { setShowCrearCliente(false); setNuevoCliente({ nombre: '', telefono: '', notas: '' }); } }}>
                <DialogContent className="sm:max-w-[420px]" data-testid="modal-crear-cliente-pos">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-amber-400" /> Nuevo cliente
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-xs text-zinc-400">Nombre *</Label>
                            <Input
                                value={nuevoCliente.nombre}
                                onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })}
                                placeholder="Nombre completo"
                                autoFocus
                                data-testid="input-nuevo-cliente-nombre"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-400">Telefono (opcional)</Label>
                            <Input
                                value={nuevoCliente.telefono}
                                onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono: e.target.value })}
                                placeholder="Telefono"
                                data-testid="input-nuevo-cliente-telefono"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-400">Notas (opcional)</Label>
                            <Input
                                value={nuevoCliente.notas}
                                onChange={(e) => setNuevoCliente({ ...nuevoCliente, notas: e.target.value })}
                                placeholder="Direccion, referencia, etc."
                                data-testid="input-nuevo-cliente-notas"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCrearCliente(false)} disabled={creandoCliente}>Cancelar</Button>
                        <Button onClick={crearClienteRapido} disabled={creandoCliente || !(nuevoCliente.nombre || '').trim()} className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="btn-guardar-cliente-pos">
                            {creandoCliente ? 'Creando...' : 'Crear y seleccionar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
